/**
 * Create Concierge — wizard with Magic Import.
 *
 * Step 0: Use case selection (icon cards)
 * Step 1: Magic Import (URL paste / file drop) OR manual details
 * Step 2: Upload documents (optional if URL was scraped)
 * Step 3: Configure behavior (pre-filled from extraction)
 * Step 4: Review & activate
 */
import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert, Box, Button, Chip, CircularProgress, FormControl, FormHelperText,
  InputLabel, LinearProgress, MenuItem, Paper, Select, Step, StepLabel,
  Stepper, TextField, Typography,
} from '@mui/material';
import {
  ArrowBack, ArrowForward, Check, CloudUpload, Celebration, Event,
  Hotel, Museum, Restaurant, AutoAwesome,
} from '@mui/icons-material';
import { conciergeApi } from '../services/concierge-api-client';
import MagicImportInput from '../components/MagicImportInput';
import type { EventSiteData } from '../types/event-data';

const STEPS = ['Use Case', 'Import Details', 'Upload Docs', 'Configure', 'Activate'];

// Must match services/chatbot_builder/constants.py MAX_UPLOAD_BYTES — the signed
// upload URL bakes this range into its signature, so the PUT must echo it.
const MAX_UPLOAD_BYTES = 25 * 1024 * 1024;

const USE_CASE_DEFS: Record<string, {
  icon: React.ReactNode; label: string; description: string;
  dateField?: { label: string; required: boolean };
  extraFields?: { key: string; label: string; placeholder: string }[];
}> = {
  wedding: { icon: <Celebration sx={{ fontSize: 32 }} />, label: 'Wedding', description: 'A concierge for your wedding guests',
    dateField: { label: 'Wedding Date', required: true },
    extraFields: [{ key: 'venue_name', label: 'Venue Name', placeholder: 'e.g. The Grand Ballroom' }] },
  event: { icon: <Event sx={{ fontSize: 32 }} />, label: 'Conference & Events', description: 'Guide attendees through your event',
    dateField: { label: 'Event Date', required: true },
    extraFields: [{ key: 'event_name', label: 'Event Name', placeholder: 'e.g. TechConf 2026' }] },
  hotel: { icon: <Hotel sx={{ fontSize: 32 }} />, label: 'Hotel & Resort', description: 'A guest concierge for your property',
    extraFields: [{ key: 'property_name', label: 'Property Name', placeholder: 'e.g. The Marina Bay Resort' }, { key: 'location', label: 'Location', placeholder: 'e.g. Miami Beach, FL' }] },
  museum: { icon: <Museum sx={{ fontSize: 32 }} />, label: 'Museum & Gallery', description: 'An interactive guide for visitors',
    extraFields: [{ key: 'museum_name', label: 'Museum Name', placeholder: 'e.g. Modern Art Museum' }] },
  restaurant: { icon: <Restaurant sx={{ fontSize: 32 }} />, label: 'Restaurant', description: 'Help diners explore your menu & story',
    extraFields: [{ key: 'restaurant_name', label: 'Restaurant Name', placeholder: 'e.g. Osteria Bella' }, { key: 'cuisine', label: 'Cuisine Type', placeholder: 'e.g. Italian, Farm-to-table' }] },
  other: { icon: <AutoAwesome sx={{ fontSize: 32 }} />, label: 'Other', description: 'Custom concierge for any use case',
    dateField: { label: 'End Date (optional)', required: false } },
};

interface UploadedFile { file: File; upload_id?: string; progress: number; status: 'pending' | 'uploading' | 'done' | 'error'; error?: string; }

export default function CreateConcierge() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState(0);
  const [autoImporting, setAutoImporting] = useState(false);
  const [error, setError] = useState('');
  const [creating, setCreating] = useState(false);
  const [useCase, setUseCase] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [eventDate, setEventDate] = useState('');
  const [extraFieldValues, setExtraFieldValues] = useState<Record<string, string>>({});
  const [allowedUseCases, setAllowedUseCases] = useState<string[]>(Object.keys(USE_CASE_DEFS));
  const [files, setFiles] = useState<UploadedFile[]>([]);
  const [instanceId, setInstanceId] = useState('');
  const [personaName, setPersonaName] = useState('AI Assistant');
  const [greeting, setGreeting] = useState('');
  const [tone, setTone] = useState('warm_friendly');
  const [voice, setVoice] = useState('');
  const [fallback, setFallback] = useState("I'm not sure about that — please contact us directly for more help.");
  // Magic Import state
  const [extractionId, setExtractionId] = useState('');
  const [usedMagicImport, setUsedMagicImport] = useState(false);
  // SMS activation state (step 4)
  const [phoneNumber, setPhoneNumber] = useState('');
  const [provisioning, setProvisioning] = useState(false);

  useEffect(() => { try { const e = JSON.parse(localStorage.getItem('studio_entitlements') || '{}'); if (e.allowed_use_cases) setAllowedUseCases(e.allowed_use_cases); } catch {} }, []);
  useEffect(() => { if (!useCase) return; const d = USE_CASE_DEFS[useCase]; if (d && !usedMagicImport) { setPersonaName(`${d.label} Concierge`); setGreeting(`Hi! I'm your ${d.label.toLowerCase()} assistant. How can I help you today?`); } }, [useCase, usedMagicImport]);

  const currentDef = useCase ? USE_CASE_DEFS[useCase] : null;
  const visibleUseCases = Object.entries(USE_CASE_DEFS).filter(([k]) => allowedUseCases.includes(k));

  // --- Magic Import callback ---
  const handleExtracted = (data: EventSiteData, extId: string) => {
    setExtractionId(extId);
    setUsedMagicImport(true);

    // Auto-fill form fields from extraction
    if (data.event_name) setName(data.event_name);
    if (data.description) setDescription(data.description);
    if (data.event_date) setEventDate(data.event_date);
    if (data.event_type) {
      // Map extracted event_type to our use case keys
      const typeMap: Record<string, string> = {
        wedding: 'wedding', conference: 'event', meetup: 'event',
        party: 'event', birthday: 'event', gala: 'event',
        hotel: 'hotel', resort: 'hotel', museum: 'museum',
        gallery: 'museum', restaurant: 'restaurant',
      };
      const mapped = typeMap[data.event_type.toLowerCase()] || useCase;
      if (mapped && USE_CASE_DEFS[mapped]) setUseCase(mapped);
    }
    if (data.venue_name) setExtraFieldValues(p => ({ ...p, venue_name: data.venue_name! }));
    if (data.suggested_persona_name) setPersonaName(data.suggested_persona_name);
    if (data.suggested_greeting) setGreeting(data.suggested_greeting);
    if (data.suggested_tone) setTone(data.suggested_tone);

    // Advance past import step to upload docs
    setStep(2);
  };

  const handleSkipImport = () => {
    // Show manual detail fields inline on step 1
    setStep(1);
  };

  // One-click deep link: /concierges/new?use_case=wedding&import_url=...&name=...&event_date=...
  // Pre-selects the use case, prefills name/date, and auto-runs Magic Import so a
  // client lands straight in the wizard with their event already populated.
  useEffect(() => {
    const uc = searchParams.get('use_case') || '';
    const importUrl = searchParams.get('import_url') || '';
    const qName = searchParams.get('name');
    const qDate = searchParams.get('event_date');
    if (qName) setName(qName);
    if (qDate) setEventDate(qDate);

    const validUc = USE_CASE_DEFS[uc] ? uc : '';
    if (!validUc) return; // No usable deep-link params — normal step 0 flow.

    setUseCase(validUc);
    setStep(1); // Skip the use-case picker.
    if (importUrl) {
      setAutoImporting(true);
      conciergeApi
        .scrapePreview(importUrl, validUc)
        .then((resp) => handleExtracted(resp.event_data, resp.extraction_id))
        .catch((e: any) =>
          setError(e.response?.data?.detail || e.message || 'Could not import that link.')
        )
        .finally(() => setAutoImporting(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- Create instance (called before upload docs / configure) ---
  // Returns the instance ID on success, or null on failure.
  const handleCreateInstance = async (): Promise<string | null> => {
    if (!name.trim()) { setError('Please enter a name.'); return null; }
    if (!useCase) { setError('Please select a use case.'); return null; }
    if (instanceId) { setStep(step + 1); return instanceId; }
    setError(''); setCreating(true);
    try {
      const r = await conciergeApi.createConcierge({
        name: name.trim(), description: description.trim(),
        use_case: useCase, event_date: eventDate || undefined,
      });
      setInstanceId(r.instance_id);
      setStep(step + 1);
      return r.instance_id;
    } catch (e: any) {
      setError(e.response?.data?.detail || e.message || 'Failed');
      return null;
    } finally { setCreating(false); }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;
    setFiles(p => [...p, ...Array.from(e.target.files!).map(f => ({ file: f, progress: 0, status: 'pending' as const }))]);
  };

  const handleUpload = async (idArg?: string) => {
    // Use the explicitly-passed id when available: right after handleCreateInstance,
    // the `instanceId` state hasn't flushed yet, so relying on it here uploads to
    // `/concierges//uploads/url` (empty id → 404). Callers pass the fresh id.
    const id = idArg || instanceId;
    if (!id) { setError('Could not resolve the concierge — please retry.'); return; }
    for (let i = 0; i < files.length; i++) {
      if (files[i].status !== 'pending') continue;
      setFiles(p => { const c = [...p]; c[i] = { ...c[i], status: 'uploading', progress: 10 }; return c; });
      try {
        const { upload_url, upload_id } = await conciergeApi.getUploadUrl(id, {
          filename: files[i].file.name,
          mime_type: files[i].file.type || 'application/octet-stream',
          file_size_bytes: files[i].file.size,
        });
        setFiles(p => { const c = [...p]; c[i] = { ...c[i], upload_id, progress: 30 }; return c; });
        const putRes = await fetch(upload_url, {
          method: 'PUT',
          headers: {
            'Content-Type': files[i].file.type || 'application/octet-stream',
            // Required: the signed URL bakes this range into its signature.
            'x-goog-content-length-range': `0,${MAX_UPLOAD_BYTES}`,
          },
          body: files[i].file,
        });
        // fetch() only rejects on network errors; an HTTP 4xx/5xx (bad
        // signature, expired URL) resolves — check res.ok or we'd mark a
        // failed upload "done" and the row stays pending forever.
        if (!putRes.ok) throw new Error(`Upload failed (${putRes.status})`);
        setFiles(p => { const c = [...p]; c[i] = { ...c[i], status: 'done', progress: 100 }; return c; });
      } catch (e: any) {
        setFiles(p => { const c = [...p]; c[i] = { ...c[i], status: 'error', error: e.message }; return c; });
      }
    }
    setStep(3);
  };

  const handleProvision = async () => {
    if (!instanceId) return;
    setError(''); setProvisioning(true);
    try {
      const r = await conciergeApi.provisionConcierge(instanceId);
      setPhoneNumber(r.phone_number);
    } catch (e: any) {
      setError(e.response?.data?.detail || e.message || 'Could not activate SMS number.');
    } finally { setProvisioning(false); }
  };

  const handleConfig = async () => {
    // Create instance first if not yet created
    const id = instanceId ?? await handleCreateInstance();
    if (!id) return; // Creation failed
    try {
      await conciergeApi.updateConfig(id, {
        persona_name: personaName, greeting, tone, voice,
        restrictions: [], fallback_message: fallback,
        // travel_features_enabled omitted -> backend resolves by use_case
        // (OFF for wedding/event so grounded answers stay clean; ON otherwise).
      });
      setStep(4);
    } catch (e: any) { setError(e.response?.data?.detail || 'Failed to save'); }
  };

  const handleNext = async () => {
    if (step === 0) {
      if (!useCase) { setError('Please select a use case.'); return; }
      setError('');
      setStep(1); // Go to magic import
    } else if (step === 1) {
      // Manual details — create instance and go to upload
      await handleCreateInstance();
    } else if (step === 2) {
      // Upload docs — create instance first if needed, then upload or skip
      if (!instanceId) {
        const id = await handleCreateInstance();
        if (!id) return;
        // Instance created, now handle files if any
        if (files.some(f => f.status === 'pending')) {
          handleUpload(id);
        } else {
          setStep(3);
        }
      } else if (files.some(f => f.status === 'pending')) {
        handleUpload(instanceId);
      } else {
        setStep(3);
      }
    } else if (step === 3) {
      handleConfig();
    }
  };

  return (
    <Box sx={{ maxWidth: 720, mx: 'auto' }}>
      <Button startIcon={<ArrowBack />} onClick={() => navigate('/concierges')} sx={{ mb: 2, color: 'text.secondary' }}>
        Back to Concierges
      </Button>
      <Typography variant="h4" fontWeight={700} gutterBottom>Create Concierge</Typography>
      <Stepper activeStep={step} sx={{ mb: 4 }}>
        {STEPS.map(l => <Step key={l}><StepLabel>{l}</StepLabel></Step>)}
      </Stepper>
      {error && <Alert severity="error" onClose={() => setError('')} sx={{ mb: 3, borderRadius: 2 }}>{error}</Alert>}

      <Paper sx={{ p: 4, borderRadius: 3 }}>
        {/* Step 0: Use Case Selection */}
        {step === 0 && (
          <Box>
            <Typography variant="h6" fontWeight={600} gutterBottom>What are you building a concierge for?</Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: '1fr 1fr 1fr' }, gap: 2, mt: 1 }}>
              {visibleUseCases.map(([key, def]) => (
                <Paper key={key} onClick={() => setUseCase(key)} sx={{
                  p: 2.5, cursor: 'pointer', border: 2,
                  borderColor: useCase === key ? 'primary.main' : 'grey.200',
                  bgcolor: useCase === key ? 'primary.main' : 'transparent',
                  color: useCase === key ? 'white' : 'text.primary',
                  borderRadius: 3, transition: 'all 0.2s',
                  '&:hover': { borderColor: 'primary.main', transform: 'translateY(-2px)', boxShadow: 2 },
                }}>
                  <Box sx={{ width: 48, height: 48, borderRadius: 2, bgcolor: useCase === key ? 'rgba(255,255,255,0.2)' : '#F8FAF9', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1.5, color: useCase === key ? 'white' : 'primary.main' }}>
                    {def.icon}
                  </Box>
                  <Typography fontWeight={600} fontSize={15}>{def.label}</Typography>
                  <Typography variant="body2" sx={{ opacity: 0.7, mt: 0.5 }}>{def.description}</Typography>
                </Paper>
              ))}
            </Box>
          </Box>
        )}

        {/* Step 1: Magic Import OR Manual Details */}
        {step === 1 && (
          autoImporting ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, py: 6 }}>
              <CircularProgress />
              <Typography variant="h6" fontWeight={600}>Importing your event details…</Typography>
              <Typography color="text.secondary" textAlign="center" maxWidth={420}>
                We're reading the link you shared and setting up your concierge. This can take up to a minute.
              </Typography>
            </Box>
          ) : (
            <MagicImportInput
              eventType={useCase}
              onExtracted={handleExtracted}
              onSkip={() => {
                // Show manual fields inline — stay on step 1 but render manual form
                // For simplicity, advance to step 2 (upload docs) with manual entry
                // The user fills in name/details as part of step 2's pre-create flow
                setStep(2);
              }}
            />
          )
        )}

        {/* Step 2: Upload Documents */}
        {step === 2 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Manual-entry users fill name/date here. Gate on usedMagicImport
                (stable) NOT !name.trim() — the latter unmounts the whole block
                the instant the user types the first character of the name. */}
            {!usedMagicImport && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mb: 2 }}>
                <Typography variant="h6" fontWeight={600}>Name your concierge</Typography>
                <TextField label="Concierge Name" value={name} onChange={e => setName(e.target.value)} required fullWidth
                  placeholder={`e.g. ${currentDef?.label || 'My'} Concierge`}
                  helperText="This is the name your guests will see" />
                <TextField label="Description" value={description} onChange={e => setDescription(e.target.value)}
                  multiline rows={2} fullWidth placeholder="Briefly describe what this concierge will help with" />
                {currentDef?.dateField && (
                  <TextField label={currentDef.dateField.label} type="date" value={eventDate}
                    onChange={e => setEventDate(e.target.value)} required={currentDef.dateField.required}
                    fullWidth InputLabelProps={{ shrink: true }}
                    helperText={currentDef.dateField.required ? 'Phone number auto-releases 30 days after' : 'Optional'} />
                )}
              </Box>
            )}

            <Typography variant="h6" fontWeight={600}>Upload your documents</Typography>
            <Typography color="text.secondary">
              {usedMagicImport
                ? 'Optionally upload additional documents to supplement the extracted data.'
                : 'Upload PDFs, Word docs, spreadsheets, or images. Your concierge uses these to answer questions.'}
            </Typography>
            <Button component="label" variant="outlined" startIcon={<CloudUpload />}
              sx={{ py: 4, borderStyle: 'dashed', borderRadius: 3 }}>
              Choose Files
              <input type="file" hidden multiple accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.pptx,.txt,.md,.png,.jpg,.jpeg,.gif,.webp" onChange={handleFileSelect} />
            </Button>
            {files.map((f, i) => (
              <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="body2" sx={{ flex: 1 }}>{f.file.name}</Typography>
                {f.status === 'uploading' && <LinearProgress sx={{ flex: 1 }} />}
                {f.status === 'done' && <Chip icon={<Check />} label="Uploaded" color="success" size="small" />}
                {f.status === 'error' && <Chip label={f.error || 'Failed'} color="error" size="small" />}
              </Box>
            ))}
          </Box>
        )}

        {/* Step 3: Configure Behavior */}
        {step === 3 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <Typography variant="h6" fontWeight={600}>Configure your concierge's personality</Typography>
            {usedMagicImport && (
              <Alert severity="success" sx={{ borderRadius: 2 }}>
                We pre-filled these from your event details. Feel free to adjust.
              </Alert>
            )}
            <TextField label="Persona Name" value={personaName} onChange={e => setPersonaName(e.target.value)}
              fullWidth helperText="The name your concierge introduces itself as" />
            <TextField label="Greeting Message" value={greeting} onChange={e => setGreeting(e.target.value)}
              multiline rows={2} fullWidth helperText="First message guests receive" />
            <FormControl fullWidth>
              <InputLabel>Tone</InputLabel>
              <Select value={tone} label="Tone" onChange={e => setTone(e.target.value)}>
                <MenuItem value="warm_friendly">Warm & Friendly</MenuItem>
                <MenuItem value="professional">Professional</MenuItem>
                <MenuItem value="casual">Casual</MenuItem>
                <MenuItem value="formal">Formal</MenuItem>
              </Select>
              <FormHelperText>How your concierge communicates</FormHelperText>
            </FormControl>
            <TextField label="Brand Voice (optional)" value={voice} onChange={e => setVoice(e.target.value)}
              multiline rows={3} fullWidth inputProps={{ maxLength: 600 }}
              placeholder="e.g. A gracious estate host who has known the couple for years — warm, understated elegance"
              helperText="Describe the personality your concierge should embody. Vivid descriptions work best; layered on top of our quality baseline." />
            <TextField label="Fallback Message" value={fallback} onChange={e => setFallback(e.target.value)}
              multiline rows={2} fullWidth helperText="Sent when no answer found" />
          </Box>
        )}

        {/* Step 4: Review & Activate */}
        {step === 4 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, py: 4 }}>
            <Box sx={{ width: 80, height: 80, borderRadius: '50%', bgcolor: '#F8FAF9', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Check sx={{ fontSize: 40, color: 'primary.main' }} />
            </Box>
            <Typography variant="h5" fontWeight={700}>Your concierge is ready!</Typography>
            {phoneNumber ? (
              <>
                <Typography color="text.secondary" textAlign="center" maxWidth={420}>
                  "{name}" is live. Guests can now text it:
                </Typography>
                <Typography variant="h5" fontWeight={700} sx={{ color: 'primary.main', letterSpacing: 1 }}>
                  {phoneNumber}
                </Typography>
                <Button variant="contained" size="large" sx={{ borderRadius: 3, px: 4, mt: 1 }}
                  onClick={() => navigate(`/concierges/${instanceId}`)}>
                  View Your Concierge
                </Button>
              </>
            ) : (
              <>
                <Typography color="text.secondary" textAlign="center" maxWidth={420}>
                  "{name}" has been created. Activate a dedicated SMS number so your guests can text it.
                </Typography>
                <Button variant="contained" size="large" sx={{ borderRadius: 3, px: 4, mt: 2 }}
                  onClick={handleProvision} disabled={provisioning}
                  endIcon={provisioning ? <CircularProgress size={16} color="inherit" /> : undefined}>
                  {provisioning ? 'Activating…' : 'Activate SMS Number'}
                </Button>
                <Button variant="text" onClick={() => navigate(`/concierges/${instanceId}`)}>
                  Skip for now — view concierge
                </Button>
              </>
            )}
          </Box>
        )}
      </Paper>

      {/* Navigation buttons */}
      {step < 4 && step !== 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3, gap: 2 }}>
          {step > 0 && <Button onClick={() => setStep(step - 1)} sx={{ borderRadius: 2 }}>Back</Button>}
          <Button
            variant="contained"
            onClick={handleNext}
            disabled={
              (step === 0 && !useCase) ||
              (step === 2 && !name.trim()) ||
              creating
            }
            endIcon={creating ? <CircularProgress size={16} /> : <ArrowForward />}
            sx={{ borderRadius: 2, px: 3 }}
          >
            {step === 0 ? 'Next' : step === 2 ? (files.length > 0 ? 'Upload & Continue' : 'Skip') : 'Save & Continue'}
          </Button>
        </Box>
      )}
    </Box>
  );
}
